import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CompletedCasescolumns } from './table_def'
import {
  Box,
  Modal,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Icon,
} from '@mui/material'
import FlipCameraAndroidOutlinedIcon from '@mui/icons-material/FlipCameraAndroidOutlined'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { DataGrid, getGridStringOperators, GridFilterItem, GridFilterModel, GridFilterOperator } from '@mui/x-data-grid'
import PatientCaseHistory from '../Common/PatientCaseHistoryModal'
import {
  fetchCompletedData,
  fetchCaseHistory,
  refreshCaseData,
  setDraftEmpty,
  amendReportUpload,
  clearPremerisApiCache,
  clearPremerisReportApiCache,
  setCaseData,
  clearSessionToken,
  saveReportData,
} from '../../../actions'
import { RootState } from '../../../reducers'
import { useStyles } from './inboxStyles'
import { CaseStatus } from './../../../utils/displayLabels'
import LaunchPremeris from '../../CaseDiagnosis/LaunchPremeris'
import CustomDatagridDateRangeFilter from '../Common/CustomDatagridDateRangeFilter'
import CustomDatagridDropDownFilter from '../Common/CustomDatagridDropDownFilter'
import SuccessSnackBar from '../../HOC/SuccesSnackBar'
import ErrorSnackBar from '../../HOC/ErrorSnackBar'

function CompletedCases({ userId }) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const completedData = useSelector((state: RootState) => state.completedData)
  const caseData = useSelector((state: RootState) => state.getCaseData)
  const [userDetails, setUserDetails] = useState()
  const { display_labels } = useSelector((state: RootState) => state.appConfig)
  const { role, user } = useSelector((state: RootState) => state.userLogin)
  const procedureData = useSelector((state: RootState) => state.getFilterProcedureCodeData)
  const [isAmendFlow, setAmendFlow] = useState(false)
  const { re_amendReport_success, re_amendReport_error } = useSelector((state: RootState) => state.saveAmendResponse)
  const gridPageSize = 10
  const totalCount = completedData.total_count
  const [premerisOpen, setPremerisOpen] = useState(false)
  const [isReadOnlyMode, setReadOnlyMode] = useState(false)
  const [isFirstPage, setFirstPage] = useState(false)
  const [pageValue, setPageValue] = useState(0)
  const [filterValue, setFilterValue] = useState('')
  const [filterName, setFilterName] = useState('updated_datetime')
  const [sortOrder, setSortOrder] = useState('desc')
  const [isFilter, setFilter] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [sortField, setSortField] = useState('updated_datetime')
  const [openSuccessSnackBar, setOpenSuccessSnackBar] = useState(false)
  const [isInitiateAmendOpen, setInitiateAmendOpen] = useState(false)
  const [columnModel, setColumnModel] = useState('updated_datetime')
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [
      {
        id: 1,
        columnField: 'quantity',
        value: [5000, 15000],
        operatorValue: 'between',
      },
    ],
  })

  const handleFilter = useCallback(
    (obj) => {
      setFilter(true)
      setFilterName(obj.filterField)
      setFilterValue(obj.filterValue)
      setStartDate(obj.startDate)
      setEndDate(obj.endDate)
      setSortField(obj.sortField ? obj.sortField : 'updated_datetime')
    },
    [isFilter],
  )

  const dateRangeOperators: GridFilterOperator[] = [
    {
      label: 'Between',
      value: 'between',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null
        }
        if (filterItem.value[0] == null || filterItem.value[1] == null) {
          return null
        }

        return ({ value }) => {
          return value !== null && filterItem.value[0] <= value && value <= filterItem.value[1]
        }
      },
      InputComponent: CustomDatagridDateRangeFilter,
      InputComponentProps: { handleFilter, sortField: sortField, caseView: 'completed' },
    },
  ]

  const dropdownOperators: GridFilterOperator[] = [
    {
      label: 'List',
      value: 'list',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null
        }
        if (filterItem.value[0] == null || filterItem.value[1] == null) {
          return null
        }

        return ({ value }) => {
          return value !== null && filterItem.value[0] <= value && value <= filterItem.value[1]
        }
      },
      InputComponent: CustomDatagridDropDownFilter,
      InputComponentProps: { handleFilter, sortField: sortField, caseView: 'completed' },
    },
  ]
  const [findUrl, setFindUrl] = useState('')
  const [isRefreshInProgress, setRefreshInProgress] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(false)

  const ProcedureCodeRenderer = {
    field: 'procedure_code',
    headerName: 'Case Type',
    width: 256,
    headerClassName: 'super-app-theme--header',
    disableClickEventBubbling: true,
    sortable: true,
    valueGetter: (params) => {
      const procedureCodeResults = procedureData.procedureCodeMap.filter((value) => {
        return value.id == params.row.procedure_code
      })
      return procedureCodeResults[0]?.display
    },
  }

  const CaseStatusRenderer = {
    field: 'workflow_stage_id',
    headerName: 'Case Status',
    sortable: true,
    width: 241,
    headerClassName: 'super-app-theme--header',
    disableClickEventBubbling: true,
    renderCell: (params) => {
      const caseStatus = CaseStatus(params.row.stage_id, role, display_labels)
      const statusArray = caseStatus.labelStr.split(', ')
      const style = { backgroundColor: caseStatus.labelColor }
      return (
        <>
          {statusArray.map((caseStatus) => (
            // eslint-disable-next-line react/jsx-key
            <>
              <Tooltip title={caseStatus} placement="top-start">
                <Stack direction="row" spacing={2}>
                  <Chip className={classes.chipSpacing} color="primary" style={style} label={caseStatus} />
                </Stack>
              </Tooltip>
            </>
          ))}
        </>
      )
    },
  }

  const ActionsRenderer = {
    field: '',
    headerName: 'Actions',
    disableColumnMenu: true,
    headerClassName: 'super-app-theme--header',
    width: 120,
    disableClickEventBubbling: true,
    filterable: false,
    renderCell: (params) => {
      return (
        <>
          <Tooltip title="Case History" placement="top-start">
            <Icon onClick={(event) => handleOpen(event, params.row)}>info_outlined</Icon>
          </Tooltip>
          <Tooltip title="Initiate Amendment" placement="top-start">
            <FlipCameraAndroidOutlinedIcon onClick={(event) => handleInitiateAmend(event, params.row.case_id)}>
              Initiate Amendment
            </FlipCameraAndroidOutlinedIcon>
          </Tooltip>
        </>
      )
    },
  }

  const ColumnsCustomFilters = (columnsData: any) =>
    useMemo(() => {
      const newColumns = [...columnsData]

      if (newColumns.length > 0) {
        const caseTypeIndex = newColumns.findIndex((col) => col.headerName === 'Case Type')
        const priorityIndex = newColumns.findIndex((col) => col.headerName === 'Priority')
        const workflowIndex = newColumns.findIndex((col) => col.headerName === 'Case Status')
        const releaseDateIndex = newColumns.findIndex((col) => col.headerName === 'Time of Release')
        const resultIndex = newColumns.findIndex((col) => col.headerName === 'Result')
        const patientNameIndex = newColumns.findIndex((col) => col.headerName === 'Patient Name')
        const trfIdIndex = newColumns.findIndex((col) => col.headerName === 'TRF ID')
        if (releaseDateIndex !== -1) {
          newColumns[releaseDateIndex] = {
            ...newColumns[releaseDateIndex],
            filterOperators: dateRangeOperators,
          }
        }
        if (resultIndex !== -1) {
          newColumns[resultIndex] = {
            ...newColumns[resultIndex],
            filterOperators: dropdownOperators,
          }
        }
        if (caseTypeIndex !== -1) {
          newColumns[caseTypeIndex] = {
            ...newColumns[caseTypeIndex],
            filterOperators: dropdownOperators,
          }
        }
        if (priorityIndex !== -1) {
          newColumns[priorityIndex] = {
            ...newColumns[priorityIndex],
            filterOperators: dropdownOperators,
          }
        }
        if (workflowIndex !== -1) {
          newColumns[workflowIndex] = {
            ...newColumns[workflowIndex],
            filterOperators: dropdownOperators,
          }
        }
        if (patientNameIndex !== -1) {
          newColumns[patientNameIndex] = {
            ...newColumns[patientNameIndex],
            filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
          }
        }
        if (trfIdIndex !== -1) {
          newColumns[trfIdIndex] = {
            ...newColumns[trfIdIndex],
            filterOperators: getGridStringOperators().filter((operator) => operator.value === 'startsWith'),
          }
        }
      }

      return newColumns
    }, [CompletedCasescolumns])

  CompletedCasescolumns.push(ProcedureCodeRenderer)
  CompletedCasescolumns.push(CaseStatusRenderer)
  CompletedCasescolumns.push(ActionsRenderer)

  useEffect(() => {
    setFindUrl(window.location.pathname)
  }, [])

  if (!userId || userId instanceof Array) return null

  const handleOpen = (event, row: any) => {
    setUserDetails(row)
    dispatch(fetchCaseHistory(row?.case_id, role))
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const ammendPremerisOpen = async (event, caseId: any) => {
    setLoadingStatus(true)
    /* Emptying the get draft report data when different case is selected, to prevent caching */
    dispatch(setDraftEmpty({ draft: {} }))
    const filterCompletedResults = completedData?.data?.filter((value) => {
      return value.case_id == caseId
    })
    const patientName = `${filterCompletedResults[0]?.patient_last_name || ''}, ${
      filterCompletedResults[0]?.patient_first_name || ''
    }`

    setRefreshInProgress(true)
    await handleRefreshCases(filterCompletedResults[0].case_number + '000')
    dispatch(
      setCaseData({
        pathologist: {
          first_name: filterCompletedResults[0].first_name,
          last_name: filterCompletedResults[0].last_name,
        },
        pathologistId: filterCompletedResults[0].user_id,
        caseNumber: filterCompletedResults[0].case_number,
        procedureCodeId: filterCompletedResults[0].procedure_code,
        workflowId: 'AMEND_INITIATED',
        patientName: patientName,
        caseId: caseId,
        nlOrderId: filterCompletedResults[0].nl_order_id,
        userCaseWorkflowId: filterCompletedResults[0].id,
        slideStatusId: filterCompletedResults[0].slide_status_id,
        mcFlag: filterCompletedResults[0].mc_flag,
        statFlag: filterCompletedResults[0].stat_flag,
      }),
    )
    setReadOnlyMode(true)
    setLoadingStatus(false)
    setPremerisOpen(true)
  }

  const handleRefreshCases = async (trfBarcode) => {
    await dispatch(refreshCaseData({ trfBarcode: trfBarcode }))
    await dispatch(clearPremerisApiCache())
    await dispatch(clearPremerisReportApiCache())
    setRefreshInProgress(false)
    setLoadingStatus(false)
  }

  const handlePageChange = async (page, details) => {
    setFirstPage(false)
    setPageValue(page)
    await dispatch(
      fetchCompletedData({
        id: `${userId}`,
        pageIndex: page + 1,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : columnModel,
        filterValue: filterValue,
        caseView: 'completed',
        startDate: startDate,
        endDate: endDate,
        sortOrder: sortOrder,
        sortField: sortField,
      }),
    )
  }

  const handlePremerisOpen = async (event, caseId: any, data) => {
    const filterCompletedResults = data.filter((value) => {
      return value.case_id == caseId
    })
    const patientName = `${filterCompletedResults[0].patient_last_name || ''}, ${
      filterCompletedResults[0].patient_first_name || ''
    }`
    setLoadingStatus(true)
    await handleRefreshCases(filterCompletedResults[0].case_number + '000')

    dispatch(
      setCaseData({
        pathologist: {
          first_name: filterCompletedResults[0].first_name,
          last_name: filterCompletedResults[0].last_name,
        },
        pathologistId: filterCompletedResults[0].user_id,
        caseNumber: filterCompletedResults[0].case_number,
        procedureCodeId: filterCompletedResults[0].procedure_code,
        workflowId: filterCompletedResults[0].stage_id,
        patientName: patientName,
        caseId: caseId,
        nlOrderId: filterCompletedResults[0].nl_order_id,
        userCaseWorkflowId: filterCompletedResults[0].id,
        slideStatusId: filterCompletedResults[0].slide_status_id,
        mcFlag: filterCompletedResults[0].mc_flag,
        statFlag: filterCompletedResults[0].stat_flag,
      }),
    )
    setLoadingStatus(false)
    setPremerisOpen(true)
  }

  const handlePremerisClose = async () => {
    updateTrfStartDataTable()
    await dispatch(clearPremerisApiCache())
    await dispatch(clearPremerisReportApiCache())
    setInitiateAmendOpen(false)
    setAmendFlow(false)
    setPremerisOpen(false)
  }

  const updateTrfStartDataTable = async () => {
    dispatch(
      fetchCompletedData({
        id: `${userId}`,
        pageIndex: pageValue + 1,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : 'updated_datetime',
        filterValue: filterValue,
        caseView: 'completed',
        startDate: startDate,
        endDate: endDate,
        sortOrder: 'desc',
        sortField: sortField,
      }),
    )
  }

  const onFilterChange = async (model: GridFilterModel) => {
    setFilterName('')
    setFilterValue('')
    setStartDate('')
    setEndDate('')
    setFilterModel(model)
    if (model.items.length && model.items[0].value) {
      await onFilterApply(model)
    } else {
      await clearFilter()
      setFilter(false)
    }
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onFilterApply = React.useCallback(async (model: GridFilterModel) => {
    if (model.items.length) {
      if (
        (model.items[0].columnField === 'patient_first_name' || model.items[0].columnField === 'case_number') &&
        model.items[0].value &&
        model.items[0].value.length
      ) {
        const val = model.items[0].value
        setFilterValue(val)
        const name = model.items[0].columnField
        setFilterName(name)
        await dispatch(
          fetchCompletedData({
            id: user,
            caseView: 'completed',
            filterField: model.items[0].columnField,
            filterValue: val,
            startDate: null,
            endDate: null,
            sortOrder: 'desc',
            pageIndex: 0,
            pageSize: 10,
            sortField: sortField,
          }),
        )
        setFilter(true)
      }
    }
  }, [])

  const clearFilter = async () => {
    await dispatch(
      fetchCompletedData({
        id: `${userId}`,
        pageIndex: pageValue,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : 'updated_datetime',
        filterValue: null,
        caseView: 'completed',
        startDate: null,
        endDate: null,
        sortOrder: 'desc',
        sortField: sortField,
      }),
    )
  }

  const handleFilteredPageChange = async (page: any, details: any) => {
    setSortOrder(details[0].sort)

    await dispatch(
      fetchCompletedData({
        id: `${userId}`,
        caseView: 'completed',
        filterField: filterName !== '' ? filterName : details[0].field,
        filterValue: filterValue,
        startDate: startDate,
        endDate: endDate,
        sortOrder: details[0].sort ? details[0].sort : 'desc',
        sortField: details[0].field,
        pageIndex: page + 1,
        pageSize: gridPageSize,
      }),
    )
  }

  const handleInitiateAmend = (event, caseId: any) => {
    setInitiateAmendOpen(true)
    setAmendFlow(true)
    setLoadingStatus(true)
    ammendPremerisOpen(event, caseId)
  }

  const handleInitiateAmendClose = () => {
    setPremerisOpen(true)
    setInitiateAmendOpen(false)
  }

  const handleTRFComplete = () => {
    dispatch(
      saveReportData({
        trfBarcode: null,
        cptCodes: [],
        saveDraftJSON: {},
        reason: '',
        diagnosisLP: {},
        consultLP: [],
        calloutDecision: false,
        procedureCode: null,
      }),
    )
    dispatch(clearSessionToken())
    setTimeout(updateDataTable, 1500)
  }

  const updateDataTable = async () => {
    setOpenSuccessSnackBar(true)
    dispatch(
      setCaseData({
        pathologist: caseData.pathologist,
        pathologistId: caseData.pathologistId,
        caseNumber: caseData.caseNumber,
        procedureCodeId: caseData.procedureCodeId,
        workflowId: 'AMEND_RESULTED_RESULT_RELEASED',
        patientName: caseData.patientName,
        caseId: caseData.caseId,
        nlOrderId: caseData.nlOrderId,
        userCaseWorkflowId: caseData.userCaseWorkflowId,
        slideStatusId: caseData.slideStatusId,
        mcFlag: caseData.mcFlag,
        statFlag: caseData.statFlag,
      }),
    )
    dispatch(
      amendReportUpload({
        pathologistId: '',
        caseNumber: '',
        caseId: '',
        nlOrderId: '',
        userCaseWorkflowId: '',
        slideStatusId: '',
        mcFlag: '',
        statFlag: '',
        userId: '',
      }),
    )
    dispatch(
      fetchCompletedData({
        id: `${userId}`,
        pageIndex: pageValue,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : 'updated_datetime',
        filterValue: null,
        caseView: 'completed',
        startDate: null,
        endDate: null,
        sortOrder: 'desc',
        sortField: sortField,
      }),
    )
    setTimeout(() => setOpenSuccessSnackBar(false), 2000)
  }

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box>
          <PatientCaseHistory userDetails={userDetails} handleClose={handleClose} />
        </Box>
      </Modal>
      <Dialog
        open={isInitiateAmendOpen}
        onClose={handleInitiateAmendClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{ paper: classes.discardDialogHeight }}
      >
        <DialogTitle className={classes.dialogTitle}>Initiating Amendment</DialogTitle>
        <DialogContent className={classes.discardDialogContent}>
          <DialogContentText id="alert-dialog-description">
            {loadingStatus && (
              <div style={{ display: 'flex' }}>
                <div style={{ marginTop: '10px' }}>
                  <CircularProgress style={{ color: '#0A688D', marginRight: '10px', height: '20px', width: '20px' }} />
                </div>
                <div>Updating the case demographic information. This will take a few moments.</div>
              </div>
            )}
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Box
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#0d688d73',
            fontWeight: '600',
          },
          '& .MuiDataGrid-columnHeader--moving': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-cell:hover': {
            cursor: 'pointer !important',
          },
          '& .MuiDataGrid-filterForm': { p: 2 },
        }}
      >
        <DataGrid
          getRowId={(row) => row?.rowId}
          disableColumnSelector
          autoHeight
          pagination
          page={isFirstPage ? 0 : pageValue}
          pageSize={10}
          paginationMode="server"
          rowCount={totalCount}
          rows={completedData.data}
          columns={ColumnsCustomFilters(CompletedCasescolumns)}
          loading={completedData.fetchInProgress}
          onPageChange={(page, details) => {
            handlePageChange(page, details)
          }}
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true
            if (params.field === '') {
              return
            } else {
              setReadOnlyMode(true)
              handlePremerisOpen(event, params.row.case_id, completedData.data)
            }
          }}
          filterModel={filterModel}
          onFilterModelChange={(model) => onFilterChange(model)}
          componentsProps={{
            filterPanel: {
              filterFormProps: {
                // Customize inputs by passing props
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  required: true,
                },
              },
              sx: {
                // Customize inputs using css selectors
                '& .MuiDataGrid-filterForm': {
                  p: 2,
                  display: 'block',
                  width: 'fit-content',
                  justifyContent: 'flex-start',
                },
                '& .MuiDataGrid-filterForm:nth-child(even)': {},
                '& .MuiDataGrid-filterFormLinkOperatorInput': { mr: 2 },
                '& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
                '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
                '& .MuiDataGrid-filterFormValueInput': { width: 'fit-content' },
              },
            },
            panel: {
              sx: {
                '& .MuiDataGrid-panelWrapper': { overflow: 'visible' },
                '& .MuiDataGrid-panelContent': { overflow: 'visible', display: 'flex' },
                '& .MuiFormControl-root': {
                  width: 'auto',
                  minWidth: '55px',
                  marginRight: '18px',
                  justifyContent: 'end',
                },
              },
            },
          }}
          onSortModelChange={(newSortModel) => {
            if (newSortModel.length) setSortField(newSortModel[0].field)
            setPageValue(0)
            handleFilteredPageChange(pageValue, newSortModel)
          }}
        />
      </Box>
      {openSuccessSnackBar && re_amendReport_success && <SuccessSnackBar message="Successfully updated case status" />}
      {openSuccessSnackBar && re_amendReport_error && <ErrorSnackBar message="Failed to update case status" />}
      <Modal
        open={premerisOpen}
        onClose={handlePremerisClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <LaunchPremeris
          caseView={'completed'}
          urlLink={findUrl}
          handlePremerisClose={handlePremerisClose}
          handleTRFComplete={handleTRFComplete}
          isReadOnlyMode={isReadOnlyMode}
          isAmendFlow={isAmendFlow}
        />
      </Modal>
      <Modal
        open={isRefreshInProgress || loadingStatus}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div>
          <CircularProgress style={{ marginLeft: '45%', marginTop: '40vh', position: 'absolute', color: '#0A688D' }} />
          <Typography
            style={{
              fontWeight: 'bolder',
              color: 'rgba(255,255,255,0.8)',
              fontSize: 'larger',
              marginLeft: '35%',
              marginTop: '47vh',
              position: 'absolute',
            }}
          >
            Refreshing the case details from LabOS...
          </Typography>
        </div>
      </Modal>
    </div>
  )
}

export default CompletedCases
